import React, { useEffect, useState, useMemo } from "react";
import ReactTooltip from "react-tooltip";

import PropTypes from "prop-types";
import { Icon } from "semantic-ui-react";

import CovertButton from "@cayuse/shared-react.components.covert-button";
import Checkbox from "@cayuse/shared-react.components.form.inputs.checkbox";
import { crunchClasses } from "@cayuse/shared-react.utils.helpers";
import {
    isFunction,
    isNonEmptyString,
} from "@cayuse/shared-react.utils.type-checks";

import { SELECT_ANY_KEY } from "globals/constants";
import {
    getFormElementsByIds,
    isFieldPicklist,
    isFieldUnitFinder,
    isFieldCheckbox,
} from "utils";

import { OptionList } from "./OptionList";
import styles from "./styles.css";
import { isSearchableField } from "./utils";

const RuleConditionSelector = ({
    onRuleChange,
    formSections,
    formElements,
    option,
    unitsOnly,
}) => {
    const sections = useMemo(
        () => Object.values(formSections) ?? [],
        [formSections]
    );
    const [selectedSection, setSelectedSection] = useState(null);
    const [selectedField, setSelectedField] = useState(null);
    const [checkedValue, setCheckedValue] = useState(null);

    const handleSectionClick = (slug) => {
        if (!checkedValue) {
            setSelectedSection(slug);
            setSelectedField(null);
        }
    };

    const handleFieldClick = (field, valueId) => {
        if (!checkedValue) {
            setSelectedField(field);
        }

        handleCheckedValueChange(valueId);
    };

    const handleCheckedValueChange = (valueId) => {
        const newValue = valueId === checkedValue ? null : valueId;
        setCheckedValue(newValue);

        if (isFunction(onRuleChange)) {
            onRuleChange(newValue);
        }
    };

    const availableFields = useMemo(() => {
        let fields = [];

        if (isNonEmptyString(selectedSection)) {
            const keys = formSections[selectedSection]?.fieldIds;
            fields = getFormElementsByIds(formElements, keys);
        }

        return fields;
    }, [formElements, formSections, selectedSection]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [selectedSection, selectedField]);

    useEffect(() => {
        const operand = option?.operand?.operand;
        if (operand?.formElementId) {
            const field = formElements.find(
                ({ id }) => id === operand.formElementId
            );

            const sectionKey = Object.keys(formSections).find((key) =>
                (formSections[key].fieldIds ?? []).includes(operand.formElementId)
            );

			if(!field || !sectionKey) {
				return
			}

            setSelectedSection(formSections[sectionKey].slug);
            setSelectedField(field);
            setCheckedValue(`${operand.formElementId}#${operand.optionId}`);
        }
    }, []);

    const isSectionWithUnits = ({ fieldIds }) => {
        const sectionFields = getFormElementsByIds(formElements, fieldIds);

        return sectionFields.some((field) => isFieldUnitFinder(field));
    };

    return (
        <div
            data-component="RuleConditionSelector"
            className={styles.ruleConditionSelector}
        >
            <div className={styles.panel}>
                <div className={styles.heading}>
                    <h5>Sections</h5>
                </div>

                <ul className={styles.optionList}>
                    {sections.map((section) => {
                        const { slug, label } = section;
                        const sectionLabel = isNonEmptyString(label)
                            ? label
                            : "Unknown Section";
                        const isActive = selectedSection === slug;
                        const isDisabled =
                            !!checkedValue ||
                            (unitsOnly && !isSectionWithUnits(section));
                        const classes = crunchClasses({
                            [styles.panelEntry]: true,
                            [styles.active]: isActive,
                            [styles.disabled]: isDisabled,
                        });

                        return (
                            <li key={`section-${slug}`} className={classes}>
                                <CovertButton
                                    type="button"
                                    className={styles.covertButton}
                                    disabled={isDisabled}
                                    onClick={() => handleSectionClick(slug)}
                                    aria-expanded={isActive}
                                >
                                    <label
                                        className={styles.label}
                                        data-tip={sectionLabel}
                                    >
                                        {sectionLabel}
                                    </label>
                                    <Icon
                                        className={styles.icon}
                                        name="chevron right"
                                    />
                                </CovertButton>
                            </li>
                        );
                    })}
                </ul>
            </div>

            <div className={styles.panel}>
                <div className={styles.heading}>
                    <h5>Questions</h5>
                </div>

                <ul className={styles.optionList}>
                    {availableFields.map((field, k) => {
                        const { id, label } = field;
                        const fieldLabel = isNonEmptyString(label)
                            ? label
                            : "Unknown Question";
                        const labelId = `optionsCheckboxLabel-${id}`;
                        const isActive = selectedField?.id === id;
                        const checkboxValue = `${id}#${SELECT_ANY_KEY}`;
                        const isChecked = checkedValue === checkboxValue;
                        const allowedForRuleType =
                            !unitsOnly || isFieldUnitFinder(field);
                        const isAnotherValueChecked =
                            !!checkedValue && !isChecked;
                        const isDisabled =
                            isAnotherValueChecked || !allowedForRuleType;
                        const hasOptions =
                            isFieldPicklist(field) || isSearchableField(field) || isFieldCheckbox(field);
                        const isOptionPanelDisabled =
                            unitsOnly || !hasOptions || !allowedForRuleType;

                        const classNames = crunchClasses({
                            [styles.panelEntry]: true,
                            [styles.active]: isActive,
                            [styles.disabled]: isDisabled,
                            [styles.questions]: true,
                        });

                        return (
                            <li key={`question-${id}`} className={classNames}>
                                <Checkbox
                                    aria-labelledby={labelId}
                                    className={styles.checkboxWrapper}
                                    checked={isChecked}
                                    disabled={isDisabled}
                                    onChange={() =>
                                        handleCheckedValueChange(checkboxValue)
                                    }
                                    data-testid={`question-checkbox-${k}`}
                                />
                                <CovertButton
                                    type="button"
                                    className={styles.covertButton}
                                    disabled={isDisabled}
                                    onClick={() =>
                                        handleFieldClick(field, checkboxValue)
                                    }
                                    aria-expanded={isActive}
                                >
                                    <label
                                        className={styles.label}
                                        id={labelId}
                                        data-tip={fieldLabel}
                                    >
                                        {fieldLabel}
                                    </label>
                                    {hasOptions && (
                                        <Icon
                                            className={`${styles.icon} ${
                                                isOptionPanelDisabled
                                                    ? styles.disabled
                                                    : ""
                                            }`}
                                            name="chevron right"
                                        />
                                    )}
                                </CovertButton>
                            </li>
                        );
                    })}
                </ul>
            </div>

            <div className={styles.panel}>
                <div className={styles.heading}>
                    <h5>Options</h5>
                </div>
                {!unitsOnly && selectedField && (
                    <OptionList
                        selectedField={selectedField}
                        checkedValue={checkedValue}
                        onChange={handleCheckedValueChange}
                    />
                )}
            </div>
        </div>
    );
};

RuleConditionSelector.propTypes = {
    onRuleChange: PropTypes.func,
};

export default RuleConditionSelector;
